import usePermissions from '@/hooks/usePermissions';
import {
  Badge,
  Box,
  Button,
  Card,
  Column,
  Icon,
  Pagination,
  Table,
  toast,
  useOpenClose,
  useTheme,
} from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import {
  AppliedDataFilter,
  HoldMotionDependentQueueTypes,
  HoldMotionDependentReviewQueueRoutes,
  paramToQueueTypeMap,
  QueueDocument,
  QueueStatus,
  QueueType,
  ReviewQueueRoute,
  SearchQueuesSort,
  UserInfo,
  UserPermissions,
} from 'types';

import { useAppDispatch } from '../../hooks';
import {
  QUEUE_FEED_INVALIDATE_DEBOUNCE_MS,
  QUEUE_FEED_INVALIDATE_JITTER_MS,
  planQueueFeedPatch,
  queuesApi,
  useGetHoldMotionProgramSettingsQuery,
  useGetQueueHoldMotionStatusQuery,
  useGetQueueListFilterQuery,
  useGetQueueListFiltersQuery,
  useGetQueuesQuery,
  useLazyGetQueueByIdQuery,
  useLazyGetQueuesQuery,
  useSubscribeToQueueFeedQuery,
  useUpdateHoldMotionSettingsMutation,
  useUpdateQueueMutation,
} from '../../services/queues';
import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { FilterBar } from '../filters';
import { AddFilterButtonDropdown } from '../filters/AddFilterButton';
import { UseFilterById } from '../filters/FilterButton';
import { SortButtonDropdown } from '../filters/SortButton';
import { MainContainer } from '../MainContainer';

import './QueueTable.css';
import PageHeader from '../PageHeader';

import { ExtraColumns } from './ExtraColumns';
import { HoldMotionTabs } from './HoldMotion/HoldMotionTabs';
import { ReleaseHoldMotionModal } from './HoldMotion/ReleaseHoldMotionModal';
import { QueuesExportAction } from './QueuesExportAction';
import { getQueueSortOptions } from './QueueSortOptions';
import { getQueueTableColumns } from './QueueTableColumns';
import { RemoveClaimModal } from './RemoveClaimModal';

const getTypeForProjectsToRelease = (type: ReviewQueueRoute) =>
  type === ReviewQueueRoute.holdMotion
    ? HoldMotionDependentQueueTypes
    : paramToQueueTypeMap[type]?.type
      ? [paramToQueueTypeMap[type].type]
      : undefined;

const flexGrowStyle = { flexGrow: 1 };
const tableScrollableConfig = {
  x: true,
  y: false,
};

const useQueueFilterById =
  (type: string): UseFilterById =>
  (id) => {
    const { data, isLoading } = useGetQueueListFilterQuery({ id, type }, { skip: !id });
    return { data, isLoading };
  };

export const Queue = () => {
  const {
    qcReviewHoldMotion = false,
    enableQcPriority = false,
    liveQueueFeed = false,
    // Deliberately separate from liveQueueFeed: that flag is being taken to 100%, and rolling it
    // back to disable patching would take the live feed down with it.
    qcQueueLivePatching = false,
  } = useFlags();
  const { type = '' } = useParams();
  const { theme } = useTheme();
  const queueProps = paramToQueueTypeMap[type];
  const extraColumns = queueProps?.extraColumns?.map((col) => ExtraColumns.find((c) => c.dataKey === col));

  const { user, claims } = useAuth();
  const userPermissions = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isOpen: isModalOpen, handleClose: handleModalClose, handleOpen: openModal } = useOpenClose();

  const {
    isOpen: isReleaseHoldMotionModalOpen,
    handleOpen: handleOpenReleaseHoldMotionModal,
    handleClose: handleCloseReleaseHoldMotionModal,
  } = useOpenClose();

  const [trigger, { data, isLoading, isFetching }] = useLazyGetQueuesQuery({});
  const isHoldMotionQueue = type === ReviewQueueRoute.holdMotion;
  const isQCSuperAdmin = userPermissions.includes(UserPermissions.lightReachQCSuperAdmin);
  const canExportQueues = isPalmettoFinanceUser(claims) && isQCSuperAdmin;
  const showHoldMotionStatus =
    qcReviewHoldMotion &&
    isQCSuperAdmin &&
    [...HoldMotionDependentReviewQueueRoutes, ReviewQueueRoute.holdMotion].includes(type as ReviewQueueRoute);
  const showHoldMotionReleaseItems = isHoldMotionQueue && isQCSuperAdmin;
  const { data: queueHoldMotion, isLoading: isQueueHoldMotionLoading } = useGetQueueHoldMotionStatusQuery(
    {
      type: queueProps?.type,
    },
    {
      skip: !queueProps?.type || !showHoldMotionStatus,
    },
  );
  // Program enablement is an orthogonal gate: a queue type configured to hold still holds nothing
  // while every program is off, so the badge reads both before claiming Hold Motion is on.
  const { data: holdMotionProgramSettings } = useGetHoldMotionProgramSettingsQuery(undefined, {
    skip: !showHoldMotionStatus,
  });
  const [updateHoldMotionSettings, { isLoading: isUpdatingHoldMotion }] = useUpdateHoldMotionSettingsMutation();
  const { data: queueListFilters = [], isLoading: isQueueListFiltersLoading } = useGetQueueListFiltersQuery({
    type,
    includeOptions: false,
  });
  const useFilterById = useQueueFilterById(type);
  const [updateQueue] = useUpdateQueueMutation();

  // Hold motion is a virtual view over its dependent queue types, so its feed subscription needs to
  // cover all of them; every other view only ever needs its own single queue type.
  const feedTypes = useMemo<QueueType[]>(
    () => (isHoldMotionQueue ? HoldMotionDependentQueueTypes : queueProps?.type ? [queueProps.type] : []),
    [isHoldMotionQueue, queueProps?.type],
  );
  const isPatchingEnabled = Boolean(liveQueueFeed && qcQueueLivePatching);
  const { data: feedState } = useSubscribeToQueueFeedQuery(
    { types: feedTypes, enabled: Boolean(liveQueueFeed), patching: isPatchingEnabled },
    { skip: !liveQueueFeed || !queueProps?.type },
  );

  const defaultData = { queues: [], total: 0 };
  const { queues, total } = data || defaultData;

  // Fed by both the list response and the live feed, whichever spoke last. Held in state rather than
  // derived so a response that omits the count (server could not compute it, or a pod mid-rollout has
  // not started attaching it) leaves the last known value on screen instead of blanking it.
  const [unfilteredTotal, setUnfilteredTotal] = useState<number | undefined>();
  useEffect(() => {
    if (typeof data?.unfilteredTotal === 'number') {
      setUnfilteredTotal(data.unfilteredTotal);
    }
  }, [data?.unfilteredTotal]);
  useEffect(() => {
    if (typeof feedState?.unfilteredTotal === 'number') {
      setUnfilteredTotal(feedState.unfilteredTotal);
    }
  }, [feedState?.unfilteredTotal]);

  const [appliedFilters, setAppliedFilters] = useState<Array<any>>([]);
  const [modalData, setModalData] = useState<Record<string, string> | undefined>();
  const [selectedQueueIds, setSelectedQueueIds] = useState<string[]>([]);
  const [selectedQueues, setSelectedQueues] = useState<QueueDocument[]>([]);
  const [releaseMode, setReleaseMode] = useState<'all' | 'selected'>('selected');
  // Snapshot of the selection taken when the release modal opens. The release acts on this frozen
  // set, so a background list refetch that clears the live selection can't change (or zero out) what
  // actually gets released.
  const [releaseSelection, setReleaseSelection] = useState<{ ids: string[]; queues: QueueDocument[] }>({
    ids: [],
    queues: [],
  });

  const currentPage = searchParams.get('page') || 1;
  const searchTerm = searchParams.get('search') || undefined;
  const advancedFilters = searchParams.get('advancedFilters');
  const isHoldQueueView = queueProps?.type === QueueType.holdMotion;
  const defaultSort = isHoldQueueView ? SearchQueuesSort.OldestOnHold : SearchQueuesSort.OldestSubmitted;
  const sort = (searchParams.get('sort') as SearchQueuesSort) || defaultSort;
  const sortOptions = useMemo(
    () => getQueueSortOptions({ queueType: queueProps?.type, enableQcPriority }),
    [enableQcPriority, queueProps?.type],
  );
  const appliedSort = sortOptions.find((opt) => opt.id === sort) ?? sortOptions[0];
  const isFirstSubmittedSort = sort === SearchQueuesSort.Oldest || sort === SearchQueuesSort.Newest;
  const isOnHoldSort = sort === SearchQueuesSort.OldestOnHold || sort === SearchQueuesSort.NewestOnHold;
  const isFiltered = !!Array.from(searchParams.keys()).filter((key) => key !== 'sort').length;
  const isQueueHoldMotionEnabled = queueHoldMotion?.isEnabled ?? false;
  const isAnyProgramHoldMotionEnabled = !!holdMotionProgramSettings?.programSettings?.some(
    (setting: { enabled: boolean }) => setting.enabled,
  );
  const isHoldingProjects = isQueueHoldMotionEnabled && isAnyProgramHoldMotionEnabled;
  const isHoldMotionLoading = isQueueHoldMotionLoading || isUpdatingHoldMotion;
  const isReleaseModeAll = releaseMode === 'all';

  // Held-queue count for the release UI. Args derive purely from `type`, so a declarative query with
  // `skip` is the right fit — RTK Query refetches when the args change and never loops on its own output.
  const typeForProjectsToRelease = getTypeForProjectsToRelease(type as ReviewQueueRoute);
  const { data: projectsToRelease } = useGetQueuesQuery(
    {
      advancedFilters: JSON.stringify([
        { id: 'status', operation: 'Equal', selectedValues: [QueueStatus.completeHoldForApproval] },
        { id: 'type', operation: 'Equal', selectedValues: typeForProjectsToRelease },
      ]),
      type: QueueType.holdMotion,
    },
    { skip: !isQueueHoldMotionEnabled || !typeForProjectsToRelease },
  );

  // Hoisted so the live-patch effect can address exactly the cache entry this view populated —
  // `updateQueryData` matches on args, so a divergent copy would silently patch nothing.
  const listArgs = useMemo(
    () => ({
      pageNum: currentPage ? Number(currentPage) : 1,
      advancedFilters: advancedFilters || JSON.stringify([]),
      searchTerm,
      sort: appliedSort?.id,
      type: queueProps?.type,
    }),
    [currentPage, advancedFilters, searchTerm, appliedSort, queueProps],
  );

  useEffect(() => {
    setSelectedQueueIds([]);
    setAppliedFilters(JSON.parse(listArgs.advancedFilters));
    trigger(listArgs);
  }, [trigger, listArgs]);

  const dispatch = useAppDispatch();
  const [fetchQueueById] = useLazyGetQueueByIdQuery();
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Read inside the event effect without making it a dependency: the effect must run once per feed
  // event, not again every time the list it just patched settles.
  const queuesRef = useRef(queues);
  queuesRef.current = queues;

  // The patching path deliberately bypasses `scheduleInvalidate` in queues.ts, so it has to carry
  // its own copy of that function's hidden-tab suppression or a backgrounded tab would keep
  // refetching on every membership event. Keyed on visibilityState (not focus) so a visible-but-
  // unfocused tab on a second monitor keeps updating.
  const pendingWhileHiddenRef = useRef(false);

  const scheduleListRefetch = useCallback(() => {
    if (document.visibilityState === 'hidden') {
      // Nobody's looking — remember a change happened and resync once when they return.
      pendingWhileHiddenRef.current = true;
      return;
    }
    if (refetchTimerRef.current) {
      return;
    }
    // Same decorrelation the signal-and-refetch path applies: without it every connected tab
    // refetches at the same offset after a push and recreates the synchronized spike.
    const jitterMs = Math.random() * QUEUE_FEED_INVALIDATE_JITTER_MS;
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null;
      dispatch(queuesApi.util.invalidateTags([{ type: 'Queues', id: 'LIST' }]));
    }, QUEUE_FEED_INVALIDATE_DEBOUNCE_MS + jitterMs);
  }, [dispatch]);

  useEffect(
    () => () => {
      if (refetchTimerRef.current) {
        clearTimeout(refetchTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    if (!isPatchingEnabled) {
      return undefined;
    }
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // A refetch queued while visible must not fire into a hidden tab — cancel it and let the
        // single resync on return cover it.
        if (refetchTimerRef.current) {
          clearTimeout(refetchTimerRef.current);
          refetchTimerRef.current = null;
          pendingWhileHiddenRef.current = true;
        }
        return;
      }
      if (pendingWhileHiddenRef.current) {
        pendingWhileHiddenRef.current = false;
        scheduleListRefetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [isPatchingEnabled, scheduleListRefetch]);

  const lastEventSequence = feedState?.lastEvent?.sequence;
  useEffect(() => {
    const lastEvent = feedState?.lastEvent;
    if (!isPatchingEnabled || !lastEvent) {
      return;
    }

    // Nothing to repaint and nobody to see it — defer the whole event to a single resync on return,
    // matching the suppression the non-patching path applies in queues.ts.
    if (document.visibilityState === 'hidden') {
      pendingWhileHiddenRef.current = true;
      return;
    }

    const { queueId } = lastEvent;
    const isVisible = !!queueId && queuesRef.current.some((queue: QueueDocument) => String(queue.id) === queueId);
    const { patchRow, dropRow, refetch } = planQueueFeedPatch(lastEvent, isVisible);

    if (dropRow) {
      dispatch(
        queuesApi.util.updateQueryData('getQueues', listArgs, (draft: any) => {
          draft.queues = draft.queues.filter((queue: QueueDocument) => String(queue.id) !== queueId);
        }),
      );
    }

    if (patchRow && queueId) {
      fetchQueueById({ id: queueId })
        .unwrap()
        .then((updated: QueueDocument) => {
          dispatch(
            queuesApi.util.updateQueryData('getQueues', listArgs, (draft: any) => {
              const index = draft.queues.findIndex((queue: QueueDocument) => String(queue.id) === queueId);
              if (index !== -1) {
                draft.queues[index] = { ...draft.queues[index], ...updated };
              }
            }),
          );
        })
        .catch(() => {
          scheduleListRefetch();
        });
    }

    if (refetch) {
      scheduleListRefetch();
    }
    // Keyed on the event sequence so repeated identical payloads still re-run exactly once each.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEventSequence]);

  const removeClaim = useCallback(
    (assignee: UserInfo, id: string) => {
      setModalData({ assigneeName: assignee.name, id });
      openModal();
    },
    [openModal],
  );

  const claimQueue = useCallback(
    async (id: string) => {
      try {
        await updateQueue({
          data: { assigneeId: user?.sub },
          id,
        }).unwrap();
        toast.success('Queue claimed');
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error claiming the queue.');
      }
    },
    [updateQueue, user],
  );

  const handleOpenReleaseSelectedModal = useCallback(() => {
    setReleaseMode('selected');
    // Freeze the selection at open time so the modal releases exactly what the user picked.
    setReleaseSelection({ ids: selectedQueueIds, queues: selectedQueues });
    handleOpenReleaseHoldMotionModal();
  }, [handleOpenReleaseHoldMotionModal, selectedQueueIds, selectedQueues]);

  const handleOpenReleaseAllModal = useCallback(() => {
    setReleaseMode('all');
    handleOpenReleaseHoldMotionModal();
  }, [handleOpenReleaseHoldMotionModal]);

  const handleReleaseHoldMotionQueues = useCallback(async () => {
    // Guard: a "Release Selected" with nothing selected must never fall through to a release-all.
    if (!isReleaseModeAll && releaseSelection.ids.length === 0) {
      toast.error('No projects selected to release.');
      return;
    }
    try {
      const queueIds = isReleaseModeAll ? undefined : releaseSelection.ids;
      const filters = advancedFilters || '';
      await updateHoldMotionSettings({
        type: queueProps?.type,
        queueIds,
        filters,
        releaseAll: isReleaseModeAll,
      }).unwrap();
      handleCloseReleaseHoldMotionModal();
      toast.success(
        `Releasing ${isReleaseModeAll ? total : releaseSelection.ids.length} held queue(s). This can take a few minutes. Reload the page in a couple minutes to see the effects.`,
        { duration: 20000 },
      );
    } catch {
      toast.error(`Failed to release hold motion items`);
    }
  }, [
    releaseSelection,
    advancedFilters,
    updateHoldMotionSettings,
    handleCloseReleaseHoldMotionModal,
    queueProps,
    isReleaseModeAll,
    total,
  ]);

  const handleApplyAdvancedFilters = useCallback(
    (filtersToApply: AppliedDataFilter[]) => {
      if (filtersToApply.some((filter) => filter.selectedValues && filter.selectedValues.length)) {
        searchParams.set('advancedFilters', JSON.stringify(filtersToApply));
        setSearchParams(searchParams);
      }
      searchParams.delete('page');
      setAppliedFilters(filtersToApply);
      setSelectedQueues([]);
    },
    [setAppliedFilters, searchParams, setSearchParams],
  );

  const handleRemoveAdvancedFilter = useCallback(
    (filterId: string) => {
      const newFilters = appliedFilters.filter((filter) => filter.id !== filterId);
      if (newFilters.length) {
        searchParams.set('advancedFilters', JSON.stringify(newFilters));
      } else {
        searchParams.delete('advancedFilters');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
      setAppliedFilters(newFilters);
      setSelectedQueues([]);
    },
    [appliedFilters, setAppliedFilters, searchParams, setSearchParams],
  );

  const handleClearAdvancedFilters = useCallback(() => {
    searchParams.delete('advancedFilters');
    searchParams.delete('page');
    setSearchParams(searchParams);
    setAppliedFilters([]);
    setSelectedQueues([]);
  }, [setAppliedFilters, searchParams, setSearchParams]);

  const handleSortSelection = useCallback(
    (sortSelection: { id: string; label: string }) => {
      searchParams.set('sort', sortSelection.id);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const onPageChange = useCallback(
    (newPage: number) => {
      searchParams.set('page', newPage.toString());
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const onSearchChange = useCallback(
    (newSearchTerm: string) => {
      if (newSearchTerm && newSearchTerm !== '') {
        searchParams.set('search', newSearchTerm);
      } else {
        searchParams.delete('search');
      }
      searchParams.delete('page');
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );

  const handleQueueRowSelection = useCallback(
    (queueIds: string[], selected: boolean) => {
      if (!queues) return;
      if (selected) {
        const newSelected: QueueDocument[] = queueIds
          .filter((id) => !selectedQueueIds.includes(id))
          .map((id) => {
            const queue = queues.find((q: QueueDocument) => String(q.id) === id);
            return queue ? queue : undefined;
          })
          .filter(Boolean) as QueueDocument[];

        setSelectedQueueIds((prev) => [...new Set([...prev, ...queueIds])]);
        setSelectedQueues((prev) => [...prev, ...newSelected]);
      } else {
        setSelectedQueueIds((prev) => prev.filter((selectedId) => !queueIds.includes(selectedId)));
        setSelectedQueues((prev) => prev.filter((selected) => !queueIds.includes(String(selected.id))));
      }
    },
    [queues, selectedQueueIds, setSelectedQueueIds, setSelectedQueues],
  );

  const handleAllQueueRowSelection = useCallback(() => {
    const queueIds = queues?.map((queue: QueueDocument) => String(queue.id)) || [];
    const shouldSelectAll = !(queues?.length && queues.length === selectedQueueIds?.length);

    handleQueueRowSelection(queueIds, shouldSelectAll);
  }, [queues, selectedQueueIds, handleQueueRowSelection]);

  const showTargetReviewTime = enableQcPriority && appliedFilters.some((f: any) => f.id === 'qcReviewTier');

  const columns = useMemo(
    () =>
      getQueueTableColumns({
        queueProps,
        extraColumns,
        removeClaim,
        claimQueue,
        handleQueueRowSelection,
        handleAllQueueRowSelection,
        selectedQueueIds,
        queues: queues || [],
        showCheckbox: isHoldMotionQueue && isQCSuperAdmin,
        qcReviewHoldMotion: qcReviewHoldMotion,
        isFirstSubmittedSort,
        isOnHoldSort,
        isHoldQueueView,
        showTargetReviewTime,
      }),
    [
      queueProps,
      extraColumns,
      removeClaim,
      claimQueue,
      handleQueueRowSelection,
      handleAllQueueRowSelection,
      selectedQueueIds,
      queues,
      isHoldMotionQueue,
      isQCSuperAdmin,
      qcReviewHoldMotion,
      isFirstSubmittedSort,
      isOnHoldSort,
      isHoldQueueView,
      showTargetReviewTime,
    ],
  );

  const filtersState = useMemo(() => ({ searchTerm }), [searchTerm]);

  const legacyQueueLink = useMemo(() => {
    if (!queueProps?.legacyType) {
      return null;
    }

    const query = queueProps.documentType ? `&documentType=${queueProps.documentType}` : '';

    return (
      <Link to={`/queues/review/${queueProps.legacyType}?status=ready${query}`}>
        Click here for older items still in review.
      </Link>
    );
  }, [queueProps?.legacyType, queueProps?.documentType]);

  const updatedQueueFilters = useMemo(() => {
    return isHoldMotionQueue ? queueListFilters.filter((filter: any) => filter.id !== 'status') : queueListFilters;
  }, [queueListFilters, isHoldMotionQueue]);
  return (
    !isLoading && (
      <>
        <ReleaseHoldMotionModal
          isOpen={isReleaseHoldMotionModalOpen}
          handleClose={handleCloseReleaseHoldMotionModal}
          handleReleaseHoldMotion={handleReleaseHoldMotionQueues}
          isLoading={isHoldMotionLoading}
          projectsToReleaseTotal={isReleaseModeAll ? total : releaseSelection.ids.length}
          selectedQueues={isReleaseModeAll ? [] : releaseSelection.queues}
          isReleaseModeAll={isReleaseModeAll}
          includesActivationPackage={feedTypes.includes(QueueType.activationPackage)}
        />
        <Helmet>
          <title>{`${queueProps?.label} Queue`}</title>
        </Helmet>
        <RemoveClaimModal isModalOpen={isModalOpen} closeModal={handleModalClose} data={modalData} />
        <MainContainer>
          <PageHeader title={`Review ${queueProps?.label} Queue`} eyebrow={legacyQueueLink}>
            <Box direction="row" gap="sm" alignItems="center">
              {canExportQueues && <QueuesExportAction queueType={queueProps?.type} sort={sort} />}
              {showHoldMotionStatus && (
                <Badge
                  variant={isHoldingProjects ? 'success' : 'default'}
                  size="sm"
                  message={
                    isHoldingProjects ? `Hold Motion On · ${projectsToRelease?.total ?? 0} Held` : 'Hold Motion Off'
                  }
                />
              )}
            </Box>
          </PageHeader>
          {isHoldMotionQueue && <HoldMotionTabs />}
          <Card>
            {isHoldMotionQueue && (
              <Box padding="lg lg 0 lg" gap="lg">
                <Box
                  background="default"
                  padding="sm lg"
                  direction="row"
                  alignItems="center"
                  radius="md"
                  width="3xl"
                  gap="sm"
                >
                  <Icon name="c-add" size="2xl" color="badge-default" />
                  <Box>
                    <Box fontSize="md">{'Projects Held'.toUpperCase()}</Box>
                    <Box fontSize="2xl" fontFamily="body" fontWeight="bold">
                      {isFetching ? '--' : total.toLocaleString('en-US')}
                    </Box>
                  </Box>
                </Box>
                {showHoldMotionReleaseItems && (
                  <Box
                    background="badge-warning"
                    borderWidth="xs"
                    borderColor="warning-600"
                    padding="sm lg"
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    radius="md"
                  >
                    <Box direction="row" gap="xs" alignItems="center">
                      <Icon name="c-warning" />
                      <Box fontSize="md">{selectedQueueIds.length} selected</Box>
                    </Box>
                    <Button
                      variant={theme === 'dark' ? 'primary' : 'secondary'}
                      tone="neutral"
                      size="sm"
                      isDisabled={!selectedQueueIds.length || isHoldMotionLoading}
                      onClick={handleOpenReleaseSelectedModal}
                    >{`Release Selected (${selectedQueueIds.length})`}</Button>
                  </Box>
                )}
              </Box>
            )}
            <Box padding="lg lg 0 lg">
              <FilterBar filtersState={filtersState} handleSearchTerm={onSearchChange}>
                <Box direction="row" style={flexGrowStyle}>
                  {!isQueueListFiltersLoading && (
                    <AddFilterButtonDropdown
                      dataFilters={updatedQueueFilters}
                      appliedFilters={appliedFilters}
                      onRemoveFilter={handleRemoveAdvancedFilter}
                      onApplyFilters={handleApplyAdvancedFilters}
                      onClearFilters={handleClearAdvancedFilters}
                      lazyFetchOptions
                      useFilterById={useFilterById}
                    />
                  )}
                  <SortButtonDropdown
                    appliedSort={appliedSort}
                    sortOptions={sortOptions}
                    onSortChange={handleSortSelection}
                    className="sortButton"
                  />
                </Box>
              </FilterBar>
              {showHoldMotionReleaseItems && (
                <Box background="info" margin="lg 0 0 0" radius="md">
                  <Box direction="row" gap="xs" alignItems="center" padding="sm lg">
                    <Box>
                      {`${selectedQueueIds.length === queues?.length ? `All ${selectedQueueIds.length}` : selectedQueueIds.length} projects on this page are selected.`}
                    </Box>
                    <Button
                      variant="tertiary"
                      size="sm"
                      onClick={handleOpenReleaseAllModal}
                      isDisabled={total === 0 || isHoldMotionLoading}
                    >{`Release all ${total} projects matching current filters`}</Button>
                  </Box>
                </Box>
              )}
              <Box
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100"
                padding="lg sm sm sm"
              >
                <Box color="body-primary" fontSize="sm" fontWeight="bold">
                  {!isFetching ? (
                    isFiltered ? (
                      <>
                        {`${total} match${queues?.length && queues.length > 1 ? 'es' : ''}`}{' '}
                        {unfilteredTotal === undefined ? '' : `of ${unfilteredTotal}`}
                      </>
                    ) : (
                      `${total} Items`
                    )
                  ) : isFiltered ? (
                    <>
                      {`-- match${queues?.length && queues.length > 1 ? 'es' : ''}`}{' '}
                      {unfilteredTotal === undefined ? '' : `of ${unfilteredTotal}`}
                    </>
                  ) : (
                    `-- Items`
                  )}
                </Box>
                {liveQueueFeed && queueProps?.type && (
                  <Badge variant="success" size="sm" message="Auto-Refresh Active" />
                )}
              </Box>
              <Table
                rowKey="id"
                columns={columns as Column[]}
                className="fade-in"
                rows={queues}
                align="left"
                isScrollable={tableScrollableConfig}
              />
            </Box>
            {queues?.length > 0 && (
              <Pagination
                activePage={Number(currentPage) || 1}
                arePagesVisible
                itemsPerPage={20}
                numberOfPagesDisplayed={3}
                onChange={onPageChange}
                totalItemsCount={total}
                isCompact
              />
            )}
          </Card>
        </MainContainer>
      </>
    )
  );
};
